import { useState, useEffect } from 'react'
import { useCreateRoundTable } from '../../hooks/useRoundTables'

interface RoundTableComposeProps {
  onClose: () => void
  onCreated: () => void
}

export function RoundTableCompose({ onClose, onCreated }: RoundTableComposeProps) {
  const [topic, setTopic] = useState('')
  const [goal, setGoal] = useState('')
  const [participants, setParticipants] = useState('')
  const [timeoutMins, setTimeoutMins] = useState('30')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateRoundTable()

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const participantList = participants
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)

    if (!topic.trim()) {
      setError('Topic is required')
      return
    }
    if (participantList.length === 0) {
      setError('At least one participant is required')
      return
    }

    try {
      await createMutation.mutateAsync({
        topic: topic.trim(),
        goal: goal.trim() || undefined,
        participants: participantList,
        timeout_minutes: parseInt(timeoutMins, 10) || 30,
      })
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create round table')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-swiss-black/40 flex items-end justify-end"
      onClick={onClose}
    >
      <div
        className="bg-swiss-white border-l border-swiss-gray-200 w-full max-w-[480px] h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-swiss-gray-200">
          <h2 className="text-[16px] font-semibold tracking-tight">Convene Round Table</h2>
          <button
            onClick={onClose}
            className="text-swiss-gray-400 hover:text-swiss-black text-[20px] leading-none cursor-pointer"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-5">
          <div>
            <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-1.5">
              TOPIC
            </label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What is this session about?"
              className="w-full px-3 py-2 text-[14px] border border-swiss-gray-200 bg-swiss-white text-swiss-black placeholder:text-swiss-gray-300 focus:outline-none focus:border-swiss-black"
            />
          </div>

          <div>
            <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-1.5">
              GOAL
            </label>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="What should participants decide or align on?"
              rows={3}
              className="w-full px-3 py-2 text-[14px] border border-swiss-gray-200 bg-swiss-white text-swiss-black placeholder:text-swiss-gray-300 focus:outline-none focus:border-swiss-black resize-none"
            />
          </div>

          <div>
            <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-1.5">
              PARTICIPANTS
            </label>
            <input
              type="text"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
              placeholder="agentdrive.gm, agentdispatch.gm"
              className="w-full px-3 py-2 text-[14px] border border-swiss-gray-200 bg-swiss-white text-swiss-black placeholder:text-swiss-gray-300 focus:outline-none focus:border-swiss-black font-swiss-mono"
            />
            <p className="text-swiss-caption text-swiss-gray-400 mt-1">Comma-separated agent IDs</p>
          </div>

          <div>
            <label className="block text-swiss-caption font-medium text-swiss-gray-600 mb-1.5">
              TIMEOUT (MINUTES)
            </label>
            <input
              type="number"
              value={timeoutMins}
              onChange={(e) => setTimeoutMins(e.target.value)}
              min="5"
              max="1440"
              className="w-full px-3 py-2 text-[14px] border border-swiss-gray-200 bg-swiss-white text-swiss-black focus:outline-none focus:border-swiss-black font-swiss-mono"
            />
          </div>

          {error && (
            <div className="text-swiss-caption text-swiss-red">{error}</div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-swiss-gray-200 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-[13px] border border-swiss-gray-200 text-swiss-gray-500 bg-transparent cursor-pointer hover:border-swiss-black hover:text-swiss-black transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="flex-1 px-4 py-2.5 text-[13px] bg-swiss-black text-swiss-white cursor-pointer hover:bg-swiss-gray-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? 'Convening...' : 'Convene'}
          </button>
        </div>
      </div>
    </div>
  )
}
